import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MyHttpClient } from '../http-client';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export const INCENTIVE_APPROVAL_PREVIEW_KEY_PREFIX = 'incentive-approval-preview:';

export interface IncentiveApprovalPreviewContext {
  requestId: string;
  requestStatus?: string;
  operation?: string;
  createdBy?: string;
  resourceId?: string;
  resource?: string;
  key?: string;
  version?: number;
  newValue: object;
  oldValue?: object;
}

@Injectable({
  providedIn: 'root'
})
export class IncentiveApprovalRequestsService {
  private readonly baseUrl = `${environment.server}/api/incentive-approval-requests`;

  constructor(private http: MyHttpClient) { }

  fetchApprovalRequestById(requestId: string) {
    return this.http.get(`${this.baseUrl}/${requestId}`)
      .pipe(map((res: any) => res.data || res.response || res))
      .pipe(catchError(this.handleError));
  }

  fetchApprovalRequests(params: {
    pageNumber: number;
    pageLimit: number;
    requestStatus?: string;
    cities?: string[];
    serviceTypes?: string[];
    types?: string[];
    operations?: string[];
    fromDate?: string;
    toDate?: string;
    sortBy?: string;
    sortOrder?: number;
  }) {
    const query = new URLSearchParams({
      pageNumber: String(params.pageNumber),
      pageLimit: String(params.pageLimit),
    });
    if (params.requestStatus) {
      query.set('requestStatus', params.requestStatus);
    }
    if (params.cities && params.cities.length) {
      query.set('cities', params.cities.join(','));
    }
    if (params.serviceTypes && params.serviceTypes.length) {
      query.set('serviceTypes', params.serviceTypes.join(','));
    }
    if (params.types && params.types.length) {
      query.set('types', params.types.join(','));
    }
    if (params.operations && params.operations.length) {
      query.set('operations', params.operations.join(','));
    }
    if (params.fromDate) {
      query.set('fromDate', params.fromDate);
    }
    if (params.toDate) {
      query.set('toDate', params.toDate);
    }
    if (params.sortBy) {
      query.set('sortBy', params.sortBy);
      query.set('sortOrder', String(params.sortOrder == null ? -1 : params.sortOrder));
    }
    return this.http.get(`${this.baseUrl}?${query.toString()}`)
      .pipe(map((res: any) => {
        const rows = res.data || res.response || [];
        return {
          data: rows,
          documentCount: res.documentCount != null ? res.documentCount : rows.length,
        };
      }))
      .pipe(catchError(this.handleError));
  }

  storePreviewForRequest(requestId: string, context: IncentiveApprovalPreviewContext | object) {
    const payload: IncentiveApprovalPreviewContext = (context as IncentiveApprovalPreviewContext).newValue != null
      ? (context as IncentiveApprovalPreviewContext)
      : { requestId, newValue: context as object };
    sessionStorage.setItem(
      `${INCENTIVE_APPROVAL_PREVIEW_KEY_PREFIX}${requestId}`,
      JSON.stringify(payload),
    );
  }

  approveRequest(requestId: string, body: { resource: string; comment?: string }) {
    return this.http.post(`${this.baseUrl}/${requestId}/approve`, body)
      .pipe(map((res: any) => res))
      .pipe(catchError(this.handleError));
  }

  rejectRequest(requestId: string, body: { resource: string; comment?: string }) {
    return this.http.post(`${this.baseUrl}/${requestId}/reject`, body)
      .pipe(map((res: any) => res))
      .pipe(catchError(this.handleError));
  }

  private handleError(error: HttpErrorResponse) {
    console.error('Failed to fetch incentive approval requests', error);
    return throwError(error);
  }
}
